import axios from 'axios';
import api from './api';
import type { InventoryCategory, InventorySubcategory } from '../types/venue';

const base = (venueId: string) => `/admin/venue/${venueId}/inventory`;

// ── Error extraction ──────────────────────────────────────────────────────────

const CATEGORY_ERROR_MESSAGES: Record<string, string> = {
  CATEGORY_HAS_SUBCATEGORIES: 'This category still has subcategories. Deactivate or move them first.',
  CATEGORY_IN_USE: 'This category is in use by one or more inventory items and cannot be deleted.',
  SUBCATEGORY_IN_USE: 'This subcategory is in use by one or more inventory items and cannot be deleted.',
  CATEGORY_NOT_FOUND: 'Category not found.',
  SUBCATEGORY_NOT_FOUND: 'Subcategory not found.',
  CATEGORY_DUPLICATE: 'A category with this name already exists at this venue.',
  SUBCATEGORY_DUPLICATE: 'A subcategory with this name already exists under this category.',
};

export function extractCategoryError(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as Record<string, unknown> | undefined;
    const errorObj = data?.['error'] as Record<string, unknown> | undefined;
    const code = errorObj?.['code'];
    if (typeof code === 'string' && CATEGORY_ERROR_MESSAGES[code]) {
      return CATEGORY_ERROR_MESSAGES[code];
    }
    const message = errorObj?.['message'] ?? data?.['message'];
    if (typeof message === 'string' && message) return message;
    if (err.response?.status === 409) return 'This action conflicts with existing data.';
    if (err.response?.status === 403) return 'You do not have permission to perform this action.';
    if (err.response?.status === 404) return 'Category not found.';
  }
  if (err instanceof Error && err.message) return err.message;
  return 'An unexpected error occurred. Please try again.';
}

// ── Payloads ────────────────────────────────────────────────────────────────────

export interface CategoryPayload {
  name?: string;
  active?: boolean;
  sortOrder?: number;
}

export interface SubcategoryPayload {
  name?: string;
  active?: boolean;
  sortOrder?: number;
}

// ── Service ─────────────────────────────────────────────────────────────────────

const inventoryCategoryService = {
  /** List categories. Optionally include inactive and nest active subcategories. */
  getCategories: async (
    venueId: string,
    opts: { includeInactive?: boolean; withSubcategories?: boolean } = {},
  ): Promise<InventoryCategory[]> => {
    const params: Record<string, unknown> = {};
    if (opts.includeInactive) params.includeInactive = 'true';
    if (opts.withSubcategories) params.withSubcategories = 'true';
    const response = await api.get(`${base(venueId)}/categories`, { params });
    return response.data?.categories ?? [];
  },

  /** Create a category. */
  createCategory: async (
    venueId: string,
    payload: { name: string; active?: boolean; sortOrder?: number },
  ): Promise<InventoryCategory> => {
    const response = await api.post(`${base(venueId)}/categories`, payload);
    return response.data;
  },

  /** Update a category (name / active / sortOrder). */
  updateCategory: async (
    venueId: string,
    categoryId: string,
    payload: CategoryPayload,
  ): Promise<InventoryCategory> => {
    const response = await api.patch(`${base(venueId)}/categories/${categoryId}`, payload);
    return response.data;
  },

  /** Hard-delete a category (409 when it has subcategories or is in use). */
  deleteCategory: async (venueId: string, categoryId: string): Promise<void> => {
    await api.delete(`${base(venueId)}/categories/${categoryId}`);
  },

  /** List subcategories for a category. */
  getSubcategories: async (
    venueId: string,
    categoryId: string,
    opts: { includeInactive?: boolean } = {},
  ): Promise<InventorySubcategory[]> => {
    const params: Record<string, unknown> = {};
    if (opts.includeInactive) params.includeInactive = 'true';
    const response = await api.get(
      `${base(venueId)}/categories/${categoryId}/subcategories`,
      { params },
    );
    return response.data?.subcategories ?? [];
  },

  /** Create a subcategory under a category. */
  createSubcategory: async (
    venueId: string,
    categoryId: string,
    payload: { name: string; active?: boolean; sortOrder?: number },
  ): Promise<InventorySubcategory> => {
    const response = await api.post(
      `${base(venueId)}/categories/${categoryId}/subcategories`,
      payload,
    );
    return response.data;
  },

  /** Update a subcategory (name / active / sortOrder). */
  updateSubcategory: async (
    venueId: string,
    subcategoryId: string,
    payload: SubcategoryPayload,
  ): Promise<InventorySubcategory> => {
    const response = await api.patch(`${base(venueId)}/subcategories/${subcategoryId}`, payload);
    return response.data;
  },

  /** Hard-delete a subcategory (409 when in use). */
  deleteSubcategory: async (venueId: string, subcategoryId: string): Promise<void> => {
    await api.delete(`${base(venueId)}/subcategories/${subcategoryId}`);
  },
};

export default inventoryCategoryService;
